import { useEffect, useMemo } from 'react';
import { useTheme } from 'styled-components';
import { useZendeskMessaging } from './ZendeskMessagingProvider';
import type { ZendeskMessagingCustomization } from './zendeskMessaging';

interface ZendeskThemeSource {
  title: string;
  colors: {
    background: string;
    backgroundCompatibilityButtons: string;
    backgroundInput: string;
    backgroundTable: string;
    cancellationButton: string;
    solidButton: string;
    textPrimary: string;
    textSecondary: string;
  };
}

export const buildZendeskMessagingCustomization = (
  theme: ZendeskThemeSource,
  hideConversationListHeader = false,
  isChatPage = false
): ZendeskMessagingCustomization => {
  const isLight = theme.title === 'light';

  return {
    common: { hideHeader: false },
    conversationList: { hideHeader: hideConversationListHeader },
    messageLog: { hideHeader: false },
    theme: {
      primary: theme.colors.solidButton,
      onPrimary: theme.colors.textSecondary,
      message: theme.colors.backgroundCompatibilityButtons,
      onMessage: theme.colors.textPrimary,
      action: theme.colors.solidButton,
      onAction: theme.colors.textSecondary,
      businessMessage: theme.colors.backgroundInput,
      onBusinessMessage: theme.colors.textPrimary,
      background: theme.colors.background,
      onBackground: theme.colors.textPrimary,
      conversationListBackground: isLight || isChatPage
        ? theme.colors.background
        : theme.colors.backgroundTable,
      onConversationListBackground: theme.colors.textPrimary,
      error: theme.colors.cancellationButton,
      onError: theme.title === 'dark'
        ? theme.colors.background
        : theme.colors.textSecondary,
      notify: theme.colors.solidButton,
      onNotify: theme.colors.textSecondary,
      onSecondaryAction: theme.colors.textPrimary
    }
  };
};

interface ZendeskMessagingThemeSyncProps {
  hideConversationListHeader?: boolean;
  isChatPage?: boolean;
}

const ZendeskMessagingThemeSync = ({
  hideConversationListHeader = false,
  isChatPage = false
}: ZendeskMessagingThemeSyncProps) => {
  const theme = useTheme();
  const { status, mode, applyCustomization } = useZendeskMessaging();
  const customization = useMemo(
    () => buildZendeskMessagingCustomization(theme, hideConversationListHeader, isChatPage),
    [hideConversationListHeader, isChatPage, theme]
  );

  useEffect(() => {
    if (
      mode !== 'embedded' ||
      !['ready', 'authenticating', 'authenticated', 'anonymous'].includes(status)
    ) {
      return;
    }
    applyCustomization(customization);
  }, [applyCustomization, customization, mode, status]);

  return null;
};

export default ZendeskMessagingThemeSync;
